
import { Timestamp } from 'firebase/firestore';

import ApplicationRequest from './ApplicationRequest';
import Competition from './Competition';
import Course, { COURSE_MEMBERSHIP_TYPES, COURSE_OWNERSHIP_TYPES } from './Course';
import Event, { ALL_ATTENDEE_OPTIONS, EVENT_REG_TYPES, EVENT_STATUSES, PARTICIPANT_ORDERS } from './Event';
import Grouping, { GROUPING_SCOPES, GROUPING_STATUSES, GROUPING_TYPES } from './Grouping';
import Guest from './Guest';
import GuestRequest, { GUEST_REQUEST_STATUSES, GUEST_REQUEST_TYPES } from './GuestRequest';
import Handbook, { HANDBOOK_STATUSES } from './Handbook';
import Invitation, { INVITATION_STATUSES } from './Invitation';
import League, { LEAGUE_STATUSES } from './League';
import Membership, { MEMBERSHIP_STATUSES } from './Membership';
import Notice, { NOTICE_STATUSES, NOTICE_TYPES, NOTICE_ERROR_LEVELS } from './Notice';
import Org, { ORG_STATUSES } from './Org';
import PersonDetails from './PersonDetails';
import Registration from './Registration';
import Season, { SEASON_STATUSES } from './Season';
import SupportAction, { SUPPORT_ACTION_STATUSES, SUPPORT_ACTION_TYPES } from './SupportAction';
import Team, { TEAM_STATUSES } from './Team';
import TeamMemberRequest, { TEAM_MEMBER_REQUEST_STATUSES } from './TeamMemberRequest';

export const DefaultObjects = {
  ApplicationRequest: {
    itemId: '',
    createTime: Timestamp.now(),
    name: '', // max 100 chars
    email: '', // max 100 chars
    ghin: '', // max 10 chars
    phone: '', // max 20 chars
    mailingAddress: '', // max 200 chars
  } as ApplicationRequest,
  Competition: {
    itemId: '',
    name: '',
  } as Competition,
  Course: {
    creatorId: '',
    createTime: Timestamp.now(),
    googlePlaceId: '',
    itemId: '',
    logo: '',
    membership: COURSE_MEMBERSHIP_TYPES.UNKNOWN,
    name: '',
    orgId: '',
    orgName: '',
    ownership: COURSE_OWNERSHIP_TYPES.UNKNOWN,
    par: 0,
    shortName: '',
    teeTimeSpacing: 0,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    usgaCourseId: '',
  } as Course,
  Grouping: {
    createTime: Timestamp.now(),
    creatorId: '',
    description: '',
    eventId: '',
    itemId: '',
    leagueId: '',
    members: [],
    name: '',
    orgId: '',
    orgName: '',
    scope: GROUPING_SCOPES.EVENT,
    seasonId: '',
    status: GROUPING_STATUSES.DRAFT,
    symbol: '',
    type: GROUPING_TYPES.FLIGHT,
    backgroundColor: '#1976d2',
  } as Grouping,
  Division: {
    createTime: Timestamp.now(),
    creatorId: '',
    description: '',
    itemId: '',
    leagueId: '',
    members: [],
    name: '',
    orgId: '',
    orgName: '',
    scope: GROUPING_SCOPES.LEAGUE,
    status: GROUPING_STATUSES.DRAFT,
    symbol: '',
    type: GROUPING_TYPES.DIVISION,
    backgroundColor: '#1976d2',
    sortOrder: 0,
  } as Grouping,
  PersonDetails: {
    itemId: '',
    creatorId: '',
    createTime: Timestamp.now(),
    name: '',
    birthday: Timestamp.fromDate(new Date('1900-01-01')),
    gender: undefined,
    localAddress: '',
    mailingAddress: '',
    phone: '',
    privacyPolicyAccepted: false,
    tosAccepted: false,
  } as PersonDetails,
  Event: {
    name: '',
    coursePairs: [],
    courseIds: [],
    areResultsPosted: false,
    competitions: [],
    creatorId: '',
    createTime: Timestamp.now(),
    days: [
      {
        courseId: '',
        datetime: Timestamp.now(),
        duration: 300,
        isGolf: true,
        isShotgun: false,
        isPractice: false,
        numHoles: 18,
        socialLocation: '',
        teeSheets: [],
        description: '',
      },
    ],
    startDatetime: Timestamp.now(),
    endDatetime: Timestamp.now(),
    description: '',
    attendeeOptions: ALL_ATTENDEE_OPTIONS,
    isFinal: false,
    isGolf: true,
    isShotgun: false,
    itemId: '',
    leagueId: /* leagueId ?? */ '',
    leagueName: '',
    logo: '',
    maxPlayers: 24,
    numTeeTimes: 6,
    orgId: /* orgId ?? */ '',
    orgName: '',
    isTeamEvent: false,
    isMemberOnly: false,
    participantOrder: PARTICIPANT_ORDERS.SIGNUP_TIME,
    registrationType: EVENT_REG_TYPES.PLAYER_NO_TEE_TIME,
    seasonId: /* seasonId ?? */ '',
    seasonName: '',
    signupOpensAt: Timestamp.now(),
    signupClosesAt: Timestamp.now(),
    socialLocation: '',
    status: EVENT_STATUSES.ACTIVE,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    // Manual override tracking - false means values can be auto-updated
    signupTimesManuallySet: false,
    ghinIndexDateManuallySet: false,
    seasonManuallyOverridden: false,
  } as Event,
  Handbook: {
    category: '',
    creatorId: '',
    creatorName: '',
    createTime: Timestamp.now(),
    description: '',
    history: [],
    isFeatured: false,
    isPublic: false,
    isPublished: false,
    itemId: '',
    logo: '',
    name: '',
    orgId: '',
    orgName: '',
    sections: [],
    tags: [],
    status: HANDBOOK_STATUSES.ACTIVE,
  } as Handbook,
  Invitation: {
    applicationId: null,
    createTime: Timestamp.now(),
    creatorId: 'system',
    email: '',
    ghin: '',
    isProcessed: false,
    isError: false,
    isPro: false,
    isAdmin: false,
    isMember: false,
    itemId: '',
    name: '',
    orgId: '',
    orgName: '',
    personId: null,
    status: INVITATION_STATUSES.PENDING,
    appDetails: {
      createTime: Timestamp.now(),
      creatorId: 'system',
      itemId: '',
      gender: undefined,
      localAddress: '',
      mailingAddress: '',
      name: '',
      phone: '',
      externalAppId: '',
      externalAppSource: '',
      clubId: '',
      clubSource: '',
      privacyPolicyAccepted: false,
      tosAccepted: false,
    } as PersonDetails,
    inviteToGratigolf: true,
    leagueInvites: [],
    teamInvites: [],
  } as Invitation,
  Guest: {
    creatorId: '',
    createTime: Timestamp.now(),
    email: '',
    guestOf: null,
    isAdmin: false,
    isMember: true,
    itemId: '',
    name: '',
    status: 'ACTIVE',
  } as Guest,
  GuestRequest: {
    creatorId: '',
    createTime: Timestamp.now(),
    eventId: '',
    guestOf: null,
    itemId: '',
    orgId: '',
    partnerNum: null,
    name: '',
    status: GUEST_REQUEST_STATUSES.REQUESTED,
    type: GUEST_REQUEST_TYPES.ADD
  } as GuestRequest,
  League: {
    name: '',
    competitionLevel: 0,
    creatorId: '',
    createTime: Timestamp.now(),
    description: '',
    includeAllOrgMembers: true,
    isCatchAll: false,
    itemId: '',
    isTeam: false,
    logo: '',
    orgName: '',
    orgId: '',
    seasonMonthDays: [
      {
        start: { month: 1, day: 1 },
        end: { month: 12, day: 31 },
      },
    ],
    status: LEAGUE_STATUSES.ACTIVE,
    // Set to yesterday so first event suggestion is today or later
    latestEventDate: Timestamp.fromDate(new Date(Date.now() - 24 * 60 * 60 * 1000)),
    // Fallback event defaults — org values overwrite these when available
    defaultEventSignupOpensDaysPrior: 6,
    defaultEventSignupClosesDaysPrior: 2,
    defaultGhinIndexSetDaysPrior: 0,
    defaultMaxPlayers: 24,
    defaultStartTime: { hour: 9, minute: 0 },
  } as League,
  Membership: {
    adminedLeagueIdsExplicit: [],
    adminedLeagueIdsImplicit: [],
    creatorId: '',
    createTime: Timestamp.now(),
    isActive: true,
    isAdmin: false,
    isMember: true,
    isPending: false,
    isPro: false,
    itemId: '',
    leagues: [],
    managedTeamIds: [],
    name: '',
    orgId: '',
    picture: null,
    status: MEMBERSHIP_STATUSES.ACTIVE,
    teams: [],
  } as Membership,
  Notice: {
    contextPairs: [],
    createTime: Timestamp.now(),
    creatorId: '',
    displayStartsAt: Timestamp.now(),
    displayStopsAt: Timestamp.fromDate(new Date(Date.now() + 24 * 60 * 60 * 1000)), // Tomorrow
    itemId: '',
    link: '',
    errorLevel: NOTICE_ERROR_LEVELS.INFO,
    type: NOTICE_TYPES.ANNOUNCEMENT,
    orgId: '',
    orgName: '',
    status: NOTICE_STATUSES.ACTIVE,
    text: '',
    title: '',
  } as Notice,
  Org: {
    catchAllLeagueId: '',
    catchAllSeasonId: '',
    createTime: Timestamp.now(),
    creatorId: '',
    defaultEventSignupOpensDaysPrior: 6,
    defaultEventSignupClosesDaysPrior: 2,
    defaultGhinIndexSetDaysPrior: 0,
    defaultStartTime: { hour: 9, minute: 0 },
    defaultMaxPlayers: 24,
    defaultSeasonMonthDays: [
      {
        start: { month: 1, day: 1 },
        end: { month: 12, day: 31 },
      },
    ],
    description: '',
    homeUrl: '',
    itemId: '',
    logo: '',
    name: '',
    notifications: [],
    shortName: '',
    status: ORG_STATUSES.ACTIVE,
  } as Org,
  Registration: {
    eventId: '',
    playerId: '',
    playerName: '',
    comment: '',
    messages: [],
    datetime: Timestamp.now(),
    isEighteen: true,
    isPlayingGame: true,
    isRiding: true,
    isRegistered: false,
    partnerIds: [],
    partnerNames: [],
  } as Registration,
  Season: {
    creatorId: '',
    createTime: Timestamp.now(),
    endTime: Timestamp.now(),
    isCatchAll: false,
    itemId: '',
    leagueId: '',
    leagueName: '',
    name: '',
    orgId: '',
    orgName: '',
    startTime: Timestamp.now(),
    status: SEASON_STATUSES.ACTIVE,
  } as Season,
  SupportAction: {
    itemId: '',
    personId: '',
    action: SUPPORT_ACTION_TYPES.VERIFY_EMAIL,
    status: SUPPORT_ACTION_STATUSES.OPEN,
    createTime: Timestamp.now(),
    lastUpdateTime: Timestamp.now(),
    history: [],
  } as SupportAction,
  Team: {
    createTime: Timestamp.now(),
    creatorId: '',
    description: '',
    itemId: '',
    leagueId: '',
    leagueName: '',
    logo: '',
    name: '',
    orgId: '',
    orgName: '',
    status: TEAM_STATUSES.ACTIVE,
  } as Team,
  TeamMemberRequest: {
    createTime: Timestamp.now(),
    email: '',
    ghin: null,
    invitationId: null,
    itemId: '',
    leagueId: '',
    leagueName: '',
    name: '',
    orgId: '',
    orgName: '',
    requesterId: '',
    requesterName: '',
    resolvedAt: null,
    resolvedBy: null,
    status: TEAM_MEMBER_REQUEST_STATUSES.PENDING,
    teamId: '',
    teamName: '',
  } as TeamMemberRequest,
};

export type SupportedTypes = keyof typeof DefaultObjects;
export default DefaultObjects;
